import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
import ApiService from '@/services/apiService';
import LambdaApiService from '@/services/apiService/lambda';
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
export default {
  name: 'CoreDtwinRuleEditConditionsWrapper',
  components: {
    codemirror
  },
  props: {
    rule: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    // the states that are to be displayed / edited
    states: {
      type: Array,
      required: true
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      conditionsTab: null,
      conditionSwitchPrompt: false,
      cmLineNumbers: false,
      scriptSyntaxError: 0
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    cmOptions() {
      return {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: this.cmLineNumbers,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      };
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    currentConditionType() {
      return this.rule.properties.conditionType;
    },
    otherConditionType() {
      return this.currentConditionType === 'script' ? 'tree' : 'script';
    },
    conditionSwitchPromptText() {
      const firstType = this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler')[`conditions${this.otherConditionType.charAt(0).toUpperCase() + this.otherConditionType.slice(1)}`];
      const secondType = this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler')[`conditions${this.currentConditionType.charAt(0).toUpperCase() + this.currentConditionType.slice(1)}`];

      // at the point when the prompt is displayed, the conditionTab-value has already been switched
      // thus, the "current" value is actually already in `otherConditionType`
      return this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.conditionSwitchPromptText', {
        firstType,
        secondType
      });
    },
    firstStateMetric() {
      return (get(this.rule, 'properties.stateMetrics[0]') || {}).key;
    },
    metricsPreview() {
      return `function (${(get(this.rule, 'properties.stateMetrics') || []).map(m => {
        return m.key;
      }).join(', ')}) {`;
    },
    stateMetrics() {
      return get(this.rule, 'properties.stateMetrics') || [];
    }
  },
  watch: {
    conditionsTab() {
      const newType = this.conditionsTab.replace('tab-', '');
      if (newType !== this.currentConditionType) {
        this.rule.properties.conditionType = newType;
        // required to clone, so `currentConditionTag`-prop gets refreshed
        // it does not notice "deep" changes otherwise
        this.rule.properties = cloneDeep(this.rule.properties);
        if (this.rule.properties.conditionType === 'script') {
          this.refreshForm();
          this.roughEvaluateConditionScript();
        }
      }
    },
    rule() {
      this.cmLineNumbers = false;
      this.setConditionsTab();
    },
    'rule.properties.conditionScript'() {
      this.roughEvaluateConditionScript();
    }
  },
  mounted() {
    this.setConditionsTab();
  },
  methods: {
    convertToJavascript: debounce(function () {
      ApiService.digitalTwinRuleConvertConditions(this, {
        conditions: this.rule.properties.conditions
      }).then(data => {
        this.rule.properties.conditionScript = `return ${data}`;
      }).catch(error => {
        console.error(error);
      });
    }, 500),
    roughEvaluateConditionScript() {
      const script = this.rule.properties.conditionScript || '';
      if (script.trim() === '' || /(\W?)return(\W)/gi.exec(script) === null) {
        this.scriptSyntaxError = this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.conditionScriptResultNotEmpty');
      } else {
        this.scriptSyntaxError = 0;
      }
    },
    evaluateConditionScript() {
      const scriptToEval = `module.exports = ${this.metricsPreview}
        ${this.rule.properties.conditionScript}
      }`;
      return LambdaApiService.evaluateScript({
        script: scriptToEval,
        scriptParams: get(this.rule, 'properties.stateMetrics', []).map(() => 42)
      }).then(res => {
        this.scriptSyntaxError = get(res, 'error');
      }).catch(error => {
        console.error(error);
      });
    },
    setConditionsTab() {
      const type = get(this.rule, 'properties.conditionType');
      if (type) {
        this.conditionsTab = `tab-${type}`;
      } else {
        this.conditionsTab = Object.keys(get(this.rule, 'properties.conditions', {})).length > 0 ? 'tab-tree' : 'tab-script';
      }
      this.rule.properties.conditionType = this.conditionsTab.replace('tab-', '');
    },
    refreshForm() {
      setTimeout(() => {
        // required workaround to get the cursor appearing at the right spot
        // https://discuss.codemirror.net/t/codemirror-cursor-is-not-partially-visible/648/6
        this.$refs.codemirror.codemirror.refresh();
        this.$refs.codemirror.codemirror.focus();
      }, 50);
    },
    onCmFocus() {
      this.refreshForm();
      this.cmLineNumbers = true;
    }
  }
};